import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { deleteOptionBinding } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { OptionBindingActions } from './OptionBindingActions';
import { optionTypeToTranslationMap } from './constants';
import {
  Header,
  HeaderDisabledChoicesTag,
  HeaderLeft,
  HeaderName,
  HeaderNameAndChoices,
  HeaderNameAndTags,
  HeaderOptionTypeTag,
  HeaderRight,
  HeaderTags,
  HeaderWrapper,
  OptionBindingDragHandle,
} from './styles';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { OptionValue, Option } from '../../types';
import { OptionChoicesWithPopover } from '../OptionHeader/OptionChoicesWithPopover';

type Props = {
  className?: string;
  draggable: boolean;
  dragging: boolean;
  offerId: string;
  onToggleOpen: () => void;
  option: Option;
  optionBindingId: string;
  onDeleteOptionBinding?: (id: string) => void;
  primaryLanguage: Language;
  selectionId: string;
};

export const OptionBindingHeader = ({
  className,
  draggable,
  dragging,
  offerId,
  onToggleOpen,
  option,
  optionBindingId,
  primaryLanguage,
  selectionId,
}: Props) => {
  const { currency } = useCountryConfigContext();
  const { vlmeConfig } = useSelectionInfoContext();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const { updateSelectionData } = useSelectionData();

  const numOfDisabledChoices = option.values.reduce((acc: number, choice: OptionValue) => {
    return choice.enabled.enabled === false ? acc + 1 : acc;
  }, 0);

  const { mutate } = useMutation(() => deleteOptionBinding(selectionId, offerId, optionBindingId), {
    onSuccess: updatedOffer => {
      updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
      toast.success(<FormattedMessage id="toast.success" />);
    },
    onError: (error: any) => {
      toast.error(<ErrorToastContent error={error} />);
    },
  });

  return (
    <HeaderWrapper
      className={className}
      onClick={isSharedVenue || !menuPermissions.contentEdit ? undefined : onToggleOpen}
      showPointer={!isSharedVenue && menuPermissions.contentEdit}
    >
      <Header showHoverStyles={!isSharedVenue && menuPermissions.contentEdit} $hidden={dragging}>
        <HeaderLeft>
          {draggable && <OptionBindingDragHandle />}
          <HeaderNameAndChoices>
            <HeaderNameAndTags>
              <HeaderName dir="auto" lang={primaryLanguage}>
                {localize(option.name, primaryLanguage)}
              </HeaderName>
              <HeaderTags>
                <HeaderOptionTypeTag>
                  <FormattedMessage id={optionTypeToTranslationMap[option.type]} />
                </HeaderOptionTypeTag>
                {numOfDisabledChoices > 0 && (
                  <HeaderDisabledChoicesTag>
                    <FormattedMessage
                      id="menu-option.disabled-choices-with-count"
                      values={{ count: numOfDisabledChoices }}
                    />
                  </HeaderDisabledChoicesTag>
                )}
              </HeaderTags>
            </HeaderNameAndTags>

            <OptionChoicesWithPopover
              currency={currency}
              onClick={isSharedVenue || !menuPermissions.contentEdit ? undefined : onToggleOpen}
              optionChoices={option.values}
              primaryLanguage={primaryLanguage}
            />
          </HeaderNameAndChoices>
        </HeaderLeft>

        {!isSharedVenue && menuPermissions.contentEdit && (
          <HeaderRight>
            <OptionBindingActions onDeleteOptionBinding={() => mutate()} />
          </HeaderRight>
        )}
      </Header>
    </HeaderWrapper>
  );
};
